import React, { useEffect, useState } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom';
import { ChevronLeft, Blocks, Plus, Pencil, Trash2, Users } from 'lucide-react';
import { v4 } from 'uuid';
import { useAlertContext } from '../contexts/Alert';
import { USER_URL, ADMIN_URL } from '../api_base.js';
import { buildHeaders } from '../utils/apiHeaders.js';

const AdminApps = () => {
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formApp, setFormApp] = useState(undefined); // undefined = closed, null = new, object = editing
    const [deleteApp, setDeleteApp] = useState(null);
    const [togglingId, setTogglingId] = useState(null);
    const [busy, setBusy] = useState(false);

    const { role } = useOutletContext() || {};
    const isAdmin = role === "admin";

    const { setAlerts } = useAlertContext();
    const navigate = useNavigate();

    const pushAlert = (text) => setAlerts(prev => ([...prev, { id: v4(), text }]));

    const fetchApps = async () => {
        try {
            const res = await fetch(USER_URL + "/apps", {
                method: "GET",
                credentials: "include",
                headers: buildHeaders(),
            });
            const data = await res.json();

            if (!res.ok || data.failed) {
                if (data.signout) navigate("/auth");
                pushAlert(data.message || "Oops! something went wrong while loading apps.");
                return;
            }

            setApps(data.data || []);
        } catch (err) {
            pushAlert("Oops! something went wrong while reaching the server.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { fetchApps(); }, []);

    const toggleMaintenance = async (app) => {
        setTogglingId(app.appId);
        try {
            const res = await fetch(ADMIN_URL + "/app-state", {
                method: "PATCH",
                credentials: "include",
                headers: buildHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify({ appId: app.appId, state: !app.inMaintenance }),
            });
            const data = await res.json();

            if (!res.ok || data.failed) {
                pushAlert(data.message || "Oops! something went wrong while updating maintenance mode.");
                return;
            }

            setApps(prev => prev.map(a => a.appId === app.appId ? { ...a, inMaintenance: !a.inMaintenance } : a));
        } catch (err) {
            pushAlert("Oops! something went wrong while reaching the server.");
        } finally {
            setTogglingId(null);
        }
    }

    const saveApp = async (formData, editingApp) => {
        setBusy(true);
        try {
            let res, data;
            if (editingApp) {
                res = await fetch(USER_URL + "/apps", {
                    method: "PATCH",
                    credentials: "include",
                    headers: buildHeaders({ "Content-Type": "application/json" }),
                    body: JSON.stringify({ appId: editingApp.appId, newAppData: formData }),
                });
            } else {
                res = await fetch(USER_URL + "/apps", {
                    method: "POST",
                    credentials: "include",
                    headers: buildHeaders({ "Content-Type": "application/json" }),
                    body: JSON.stringify({ appData: formData }),
                });
            }
            data = await res.json();

            if (!res.ok || data.failed) {
                pushAlert(data.message || "Oops! something went wrong while saving this app.");
                return;
            }

            pushAlert(editingApp ? "App updated successfully." : "App created successfully.");
            setFormApp(undefined);
            await fetchApps();
        } catch (err) {
            pushAlert("Oops! something went wrong while reaching the server.");
        } finally {
            setBusy(false);
        }
    }

    const confirmDelete = async () => {
        setBusy(true);
        try {
            const res = await fetch(USER_URL + "/apps", {
                method: "DELETE",
                credentials: "include",
                headers: buildHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify({ appId: deleteApp.appId }),
            });
            const data = await res.json();

            if (!res.ok || data.failed) {
                pushAlert(data.message || "Oops! something went wrong while removing this app.");
                return;
            }

            setApps(prev => prev.filter(a => a.appId !== deleteApp.appId));
            pushAlert("App removed successfully.");
            setDeleteApp(null);
        } catch (err) {
            pushAlert("Oops! something went wrong while reaching the server.");
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="page">
            <div className="page_header">
                <button className="back" onClick={() => navigate("/admin")}><ChevronLeft /></button>
                <div className="page_title">
                    <h2>Apps</h2>
                    <p>{apps.length} apps · manage maintenance and access.</p>
                </div>
            </div>

            <div className="page_body">
                {!isAdmin ? (
                    <div className="viewer_note">Viewer mode — you can browse apps, but creating, editing, and maintenance toggles are admin-only.</div>
                ) : (
                    <div className="toolbar">
                        <div />
                        <button className="pill_button" onClick={() => setFormApp(null)}>
                            <Plus size="16" /> New App
                        </button>
                    </div>
                )}

                {loading ? (
                    <div className="empty_state"><span className="loading"></span></div>
                ) : apps.length === 0 ? (
                    <div className="empty_state">
                        <Blocks size="40" />
                        <h3>No apps yet</h3>
                    </div>
                ) : (
                    <div className="devices_list">
                        {apps.map(app => (
                            <div className="app_row_card" key={app.appId}>
                                {app.appIcon ? <img src={app.appIcon} alt="" /> : <div className="app_row_icon"><Blocks size="20" /></div>}
                                <div className="app_row_meta">
                                    <h4>{app.appName}</h4>
                                    <span>{app.appType} · <Users size="10" style={{ verticalAlign: "-1px" }} /> {app.usersCount ?? 0} users</span>
                                </div>
                                <div className="app_row_actions">
                                    <span className={`badge ${app.inMaintenance ? "suspended" : "active"}`}>
                                        {app.inMaintenance ? "Maintenance" : "Live"}
                                    </span>
                                    {isAdmin ? (
                                        <>
                                            <button
                                                className={`toggle_switch ${app.inMaintenance ? "on" : ""}`}
                                                onClick={() => toggleMaintenance(app)}
                                                disabled={togglingId === app.appId}
                                                title="Toggle maintenance"
                                            >
                                                <span className="knob"></span>
                                            </button>
                                            <button className="icon_button" onClick={() => setFormApp(app)}><Pencil size="15" /></button>
                                            <button className="icon_button danger" onClick={() => setDeleteApp(app)}><Trash2 size="15" /></button>
                                        </>
                                    ) : null}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {formApp !== undefined ? (
                <AppFormModal
                    app={formApp}
                    busy={busy}
                    onCancel={() => setFormApp(undefined)}
                    onSave={saveApp}
                />
            ) : null}

            {deleteApp ? (
                <div className="modal_backdrop" onClick={() => !busy && setDeleteApp(null)}>
                    <div className="modal_card" onClick={(e) => e.stopPropagation()}>
                        <h3>Remove {deleteApp.appName}?</h3>
                        <p style={{ color: "#ffffff90", fontSize: 13 }}>This permanently deletes the app and cannot be undone.</p>
                        <div className="modal_actions">
                            <button className="pill_button ghost" onClick={() => setDeleteApp(null)} disabled={busy}>Cancel</button>
                            <button className="pill_button danger" onClick={confirmDelete} disabled={busy}>
                                {busy ? <span className="loading"></span> : "Remove"}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    )
}

const AppFormModal = ({ app, busy, onCancel, onSave }) => {
    const [appName, setAppName] = useState(app?.appName || "");
    const [appLink, setAppLink] = useState(app?.appLink || "");
    const [appIcon, setAppIcon] = useState(app?.appIcon || "");
    const [appType, setAppType] = useState(app?.appType || "frontend");

    const submit = (e) => {
        e.preventDefault();
        const formData = { appName: appName.trim(), appType };
        if (appLink.trim()) formData.appLink = appLink.trim();
        if (appIcon.trim()) formData.appIcon = appIcon.trim();
        onSave(formData, app);
    }

    return (
        <div className="modal_backdrop" onClick={() => !busy && onCancel()}>
            <div className="modal_card" onClick={(e) => e.stopPropagation()}>
                <h3>{app ? `Edit ${app.appName}` : "New App"}</h3>
                <form onSubmit={submit}>
                    <div className="field_container">
                        <label>App Name</label>
                        <div className="input"><input value={appName} onChange={(e) => setAppName(e.target.value)} required /></div>
                    </div>
                    <div className="field_container">
                        <label>App Link</label>
                        <div className="input"><input type="url" value={appLink} onChange={(e) => setAppLink(e.target.value)} placeholder="https://…" /></div>
                    </div>
                    <div className="field_container">
                        <label>Icon URL</label>
                        <div className="input"><input type="url" value={appIcon} onChange={(e) => setAppIcon(e.target.value)} placeholder="https://…" /></div>
                    </div>
                    <div className="field_container">
                        <label>Type</label>
                        <select className="select_input" value={appType} onChange={(e) => setAppType(e.target.value)}>
                            <option value="frontend">Frontend</option>
                            <option value="backend">Backend</option>
                        </select>
                    </div>
                    <div className="modal_actions">
                        <button type="button" className="pill_button ghost" onClick={onCancel} disabled={busy}>Cancel</button>
                        <button type="submit" className="pill_button" disabled={busy}>
                            {busy ? <span className="loading"></span> : "Save"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default AdminApps;
